import { parse } from 'csv-parse';
import { setIn } from 'formik';
import { SYSTEM_DATE_FORMAT } from '../../constants';
import { dateFormat, translate } from '../../utils';

export const COLUMN_HEADERS_TRANSLATION_KEYS = {
    roomKey: 'ROOM_TBL',
    category: 'CAT_TBL',
    clientTitleId: 'CLIENT_TITLE',
    lastname: 'LN',
    firstname: 'FN',
    street: 'CLIENT_STREET',
    country: 'COUNTRY_NAME',
    regionId: 'REGION_NAME',
    zipcode: 'ZIPCODE',
    city: 'CITY_NAME',
    language: 'LANGUAGE_NAME',
    email: 'EMAIL',
    telephone: 'CLIENT_TELEPHONE',
    mobile: 'CLIENT_MOBILE',
    fax: 'CLIENT_FAX',
    vipStatusId: 'CLIENT_VIP_STATUS',
    nationality: 'CLIENT_NATIONALITY',
    passportNumber: 'CLIENT_PASSPORT',
    idCardNumber: 'CLIENT_IDCARD',
    issuingAuthority: 'ISSUE_AUTHY',
    birthday: 'CLIENT_DOB',
    carPlateNumber: 'CLIENT_EDIT_FORM_CAR_PLATE_NUMBER',
    stayPreferences: 'STAY_PREFERENCES',
    mealPreferences: 'MEAL_PREFERENCES',
    comments: 'CLIENT_COMMENTS',
};

/**
 * @param {Record<string, string>} map
 * @returns {Array<{ value: string, label: string }>}
 */
export function mapToOptions(map) {
    return [
        { value: '', label: '' },
        ...Object.entries(map).map(([value, label]) => ({ value: String(value), label: String(label) })),
    ];
}

/**
 * @param {string[]} guestColumnKeys
 * @returns {string[]}
 */
export function buildTemplateHeaderRow(guestColumnKeys) {
    const roomCategoryHeader = `${translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS.roomKey)} | ${translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS.category)}`;

    return [roomCategoryHeader, ...guestColumnKeys.map((guestColumnKey) => translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS[guestColumnKey]))];
}

/**
 * @param {Array<{ roomName: string, categoryName: string, maxOccupancy: number, guests: Record<string, unknown>[] }>} rooms
 * @param {string[]} guestColumnKeys
 * @returns {string[][]}
 */
export function buildTemplateGuestRowsWithoutData(rooms, guestColumnKeys) {
    const dataRows = [];
    const emptyGuestCells = guestColumnKeys.map(() => '');

    for (let roomIndex = 0; roomIndex < rooms.length; roomIndex++) {
        const room = rooms[roomIndex];
        const roomLabel = getRoomNameAndCategoryLabelWithOccurrenceCount(rooms, roomIndex);

        for (let rowIndex = 0; rowIndex < room.maxOccupancy; rowIndex++) {
            dataRows.push([roomLabel, ...emptyGuestCells]);
        }
    }

    return dataRows;
}

/**
 * @param {Array<{ roomName: string, categoryName: string, maxOccupancy: number, guests: Record<string, unknown>[] }>} rooms
 * @param {string[]} guestColumnKeys
 * @param {{
 *   clientTitlesMap: Record<string, string>,
 *   countriesMap: Record<string, string>,
 *   languageMap: Record<string, string>,
 *   vipStatusMap: Record<string, string>,
 *   regionMaps: Record<string, Record<string, string>>
 * }} maps
 * @returns {string[][]}
 */
export function buildTemplateGuestRowsWithData(rooms, guestColumnKeys, maps) {
    const dataRows = [];
    const emptyGuestCells = guestColumnKeys.map(() => '');

    for (let roomIndex = 0; roomIndex < rooms.length; roomIndex++) {
        const room = rooms[roomIndex];
        const roomLabel = getRoomNameAndCategoryLabelWithOccurrenceCount(rooms, roomIndex);

        const roomDataRows = room.guests.map((guest) => [
            roomLabel,
            ...getGuestDataForTemplate(guest, guestColumnKeys, maps),
        ]);

        while (roomDataRows.length < room.maxOccupancy) {
            roomDataRows.push([roomLabel, ...emptyGuestCells]);
        }

        dataRows.push(...roomDataRows);
    }

    return dataRows;
}

/**
 * @param {Array<{ roomName: string, categoryName: string }>} rooms
 * @param {number} roomIndex
 * @returns {string}
 */
export function getRoomNameAndCategoryLabelWithOccurrenceCount(rooms, roomIndex) {
    const room = rooms[roomIndex];
    const baseLabel = `${room.roomName} | ${room.categoryName}`;
    let occurrenceCount = 0;

    for (let index = 0; index <= roomIndex; index++) {
        if (rooms[index].roomName === room.roomName && rooms[index].categoryName === room.categoryName) {
            occurrenceCount++;
        }
    }

    if (occurrenceCount === 1) {
        return baseLabel;
    }

    return `${baseLabel} —${occurrenceCount}`; // em dash character
}

/**
 * @param {Record<string, unknown>} guest
 * @param {string[]} guestColumnKeys
 * @param {{
 *   clientTitlesMap: Record<string, string>,
 *   countriesMap: Record<string, string>,
 *   languageMap: Record<string, string>,
 *   vipStatusMap: Record<string, string>,
 *   regionMaps: Record<string, Record<string, string>>
 * }} maps
 * @returns {string[]}
 */
function getGuestDataForTemplate(guest, guestColumnKeys, maps) {
    const { clientTitlesMap, countriesMap, languageMap, vipStatusMap, regionMaps } = maps;

    return guestColumnKeys.map((guestColumnKey) => {
        const rawValue = guest[guestColumnKey] ?? '';
        if (rawValue === '') return '';

        if (guestColumnKey === 'clientTitleId') return clientTitlesMap[rawValue] ?? '';
        if (guestColumnKey === 'country') return countriesMap[rawValue] ?? '';
        if (guestColumnKey === 'regionId') {
            const regionMap = guest.country !== '' ? (regionMaps[guest.country] ?? {}) : {};
            return regionMap[rawValue] ?? '';
        }
        if (guestColumnKey === 'language') return languageMap[rawValue] ?? '';
        if (guestColumnKey === 'vipStatusId') return vipStatusMap[rawValue] ?? '';
        if (guestColumnKey === 'nationality') return countriesMap[rawValue] ?? '';
        if (guestColumnKey === 'birthday') return dateFormat(rawValue);

        return rawValue;
    });
}

/**
 * @param {string} fileText
 * @param {string[]} guestColumnKeys
 * @param {Array<{ roomName: string, categoryName: string, maxOccupancy: number } & Record<string, unknown>>} rooms
 * @param {number} maxGuestsPerRoom
 * @returns {{ ok: true, rawNextRooms: Array<Record<string, unknown>>, invalidRoomNameAndCategoryLabels: Set<string>, overflowedRoomNameAndCategoryLabels: Set<string> } | { ok: false, errorTranslationKey: string }}
 */
export function buildRawNextRoomsFromGuestsImport(fileText, guestColumnKeys, rooms, maxGuestsPerRoom) {
    const expectedColumnsCount = 1 + guestColumnKeys.length;

    let records = null;
    let hadParseException = false;
    let hasWrongColumnsCount = false;

    try {
        const candidateRecords = parse(fileText, {
            bom: true,
            delimiter: [',',';'],
            skip_empty_lines: true,
            skip_records_with_empty_values: true,
        });

        if (candidateRecords.length >= 2) {
            hasWrongColumnsCount = candidateRecords.some((candidateRecord) => candidateRecord.length !== expectedColumnsCount);

            if (!hasWrongColumnsCount) {
                records = candidateRecords;
            }
        }
    } catch {
        hadParseException = true;
    }

    if (records === null) {
        if (hadParseException) {
            return { ok: false, errorTranslationKey: 'ADD_GUESTS_UPLOAD_PARSE_ERROR' };
        }

        if (hasWrongColumnsCount) {
            return { ok: false, errorTranslationKey: 'ADD_GUESTS_UPLOAD_COLUMN_COUNT_MISMATCH' };
        }

        return { ok: false, errorTranslationKey: 'ADD_GUESTS_UPLOAD_NO_DATA_ROWS' };
    }

    const validRoomNameAndCategoryLabels = new Set(rooms.map((_, roomIndex) => getRoomNameAndCategoryLabelWithOccurrenceCount(rooms, roomIndex)));
    const invalidRoomNameAndCategoryLabels = new Set();
    const overflowedRoomNameAndCategoryLabels = new Set();

    /** @type {Map<string, string[][]>} */
    const guestsPerRoomNameAndCategory = new Map();
    const dataRows = records.slice(1);

    for (const dataRow of dataRows) {
        const roomNameAndCategory = dataRow[0];
        if (!validRoomNameAndCategoryLabels.has(roomNameAndCategory)) {
            invalidRoomNameAndCategoryLabels.add(roomNameAndCategory);
            continue;
        }

        const guestData = dataRow.slice(1).map((cell) => String(cell).trim());
        if (guestData.every((cellData) => cellData === '')) continue;

        const guestsInRoomNameAndCategory = guestsPerRoomNameAndCategory.get(roomNameAndCategory) ?? [];
        guestsInRoomNameAndCategory.push(guestData);
        guestsPerRoomNameAndCategory.set(roomNameAndCategory, guestsInRoomNameAndCategory);
    }

    const rawNextRooms = rooms.map((room, roomIndex) => {
        const roomNameAndCategory = getRoomNameAndCategoryLabelWithOccurrenceCount(rooms, roomIndex);
        const guestsInRoomNameAndCategory = guestsPerRoomNameAndCategory.get(roomNameAndCategory) ?? [];

        const guests = [];
        while (guests.length < maxGuestsPerRoom && guestsInRoomNameAndCategory.length > 0) {
            guests.push(createGuest(guestsInRoomNameAndCategory.shift(), guestColumnKeys));
        }

        if (guests.length === maxGuestsPerRoom && guestsInRoomNameAndCategory.length > 0) {
            overflowedRoomNameAndCategoryLabels.add(roomNameAndCategory);
        }

        if (guests.length < room.maxOccupancy) {
            guests.push(createGuest([], guestColumnKeys));
        }

        return { ...room, guests };
    });

    return {
        ok: true,
        rawNextRooms,
        invalidRoomNameAndCategoryLabels,
        overflowedRoomNameAndCategoryLabels,
    };
}

/**
 * @param {string[]} guestData
 * @param {string[]} guestColumnKeys
 */
function createGuest(guestData, guestColumnKeys) {
    const guest = { clientKey: '' };

    guestColumnKeys.forEach((guestColumnKey, columnIndex) => {
        guest[guestColumnKey] = guestData[columnIndex] ?? '';
    });

    return guest;
}

/**
 * @param {Array<{ guests: Record<string, string>[] } & Record<string, unknown>>} nextRooms
 * @returns {{ rooms: typeof nextRooms, fieldErrors: Record<string, unknown> }}
 */
export function convertBirthdaysToSystemFormat(nextRooms) {
    const propertyDateFormat = window.config.basicSettings.setting_default_dateformat.toUpperCase();

    let fieldErrors = {};

    const rooms = nextRooms.map((room, roomIndex) => ({
        ...room,
        guests: room.guests.map((guest, guestIndex) => {
            const rawBirthday = guest.birthday;

            if (rawBirthday === '') {
                return { ...guest, birthday: '' };
            }

            const parsedBirthday = moment(rawBirthday, propertyDateFormat, true);

            if (!parsedBirthday.isValid()) {
                fieldErrors = setIn(
                    fieldErrors,
                    `rooms.${roomIndex}.guests.${guestIndex}.birthday`,
                    translate('frontend', 'ADD_GUESTS_UPLOAD_BIRTHDAY_FORMAT_ERROR', { value: rawBirthday }),
                );

                return { ...guest, birthday: '' };
            }

            return { ...guest, birthday: parsedBirthday.format(SYSTEM_DATE_FORMAT) };
        }),
    }));

    return { rooms, fieldErrors };
}

/**
 * @param {Array<{ guests: Record<string, string>[] } & Record<string, unknown>>} nextRooms
 * @param {{
 *   clientTitlesMap: Record<string, string>,
 *   countriesMap: Record<string, string>,
 *   languageMap: Record<string, string>,
 *   vipStatusMap: Record<string, string>
 * }} maps
 * @param {Record<string, unknown>} [previousFieldErrors]
 * @returns {{ rooms: typeof nextRooms, fieldErrors: Record<string, unknown> }}
 */
export function resolveGuestImportValuesToStaticMapKeys(nextRooms, maps, previousFieldErrors = {}) {
    const { clientTitlesMap, countriesMap, languageMap, vipStatusMap } = maps;

    let fieldErrors = previousFieldErrors;

    const rooms = nextRooms.map((room, roomIndex) => ({
        ...room,
        guests: room.guests.map((guest, guestIndex) => {
            /**
             * @param {string} fieldKey
             * @param {string} rawValue
             * @param {Record<string, string>} map
             */
            const resolveGuestImportField = (fieldKey, rawValue, map) => {
                const resolvedValue = resolveValueToLabel(rawValue, map);

                if (resolvedValue === false) {
                    fieldErrors = setIn(
                        fieldErrors,
                        `rooms.${roomIndex}.guests.${guestIndex}.${fieldKey}`,
                        translate('frontend', 'ADD_GUESTS_UPLOAD_UNMAPPED_FIELD', {
                            fieldLabel: translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS[fieldKey]),
                            value: rawValue,
                        }),
                    );
                }

                return resolvedValue || '';
            };

            return {
                ...guest,
                clientTitleId: resolveGuestImportField('clientTitleId', guest.clientTitleId, clientTitlesMap),
                country: resolveGuestImportField('country', guest.country, countriesMap),
                nationality: resolveGuestImportField('nationality', guest.nationality, countriesMap),
                language: resolveGuestImportField('language', guest.language, languageMap),
                vipStatusId: resolveGuestImportField('vipStatusId', guest.vipStatusId, vipStatusMap),
            };
        }),
    }));

    return { rooms, fieldErrors };
}

/**
 * @param {Array<{ guests: Record<string, string>[] } & Record<string, unknown>>} nextRooms
 * @param {Record<string, Record<string, string>>} regionMaps
 * @param {Record<string, unknown>} previousFieldErrors
 * @returns {{ rooms: typeof nextRooms, fieldErrors: Record<string, unknown> }}
 */
export function resolveGuestImportRegions(nextRooms, regionMaps, previousFieldErrors) {
    let fieldErrors = previousFieldErrors;

    const rooms = nextRooms.map((room, roomIndex) => ({
        ...room,
        guests: room.guests.map((guest, guestIndex) => {
            const regionMap = guest.country !== '' ? (regionMaps[guest.country] ?? {}) : {};
            const resolvedValue = resolveValueToLabel(guest.regionId, regionMap);

            if (resolvedValue === false) {
                fieldErrors = setIn(
                    fieldErrors,
                    `rooms.${roomIndex}.guests.${guestIndex}.regionId`,
                    translate('frontend', 'ADD_GUESTS_UPLOAD_UNMAPPED_FIELD', {
                        fieldLabel: translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS.regionId),
                        value: guest.regionId,
                    }),
                );
            }

            return { ...guest, regionId: resolvedValue || '' };
        }),
    }));

    return { rooms, fieldErrors };
}

/**
 * @param {string} value
 * @param {Record<string, string>} map
 * @returns {string | false}
 */
function resolveValueToLabel(value, map) {
    if (value === '') return '';

    const normalizedValue = value.toLowerCase();

    for (const [key, label] of Object.entries(map)) {
        if (label.toLowerCase() === normalizedValue) {
            return key;
        }
    }

    return false;
}

/**
 * @param {Record<string, unknown> | undefined} previousErrors
 * @param {Array<{ guests: Array<Record<string, unknown>> }>} rooms
 * @param {number} updateRoomIndex
 * @param {number} updatedGuestIndex
 * @param {string} fieldKey
 * @returns {Record<string, unknown> | undefined}
 */
export function clearFormikErrorsForGuestField(
    previousErrors,
    rooms,
    updateRoomIndex,
    updatedGuestIndex,
    fieldKey,
) {
    const updatedGuest = rooms[updateRoomIndex].guests[updatedGuestIndex];
    const clientKey = updatedGuest.clientKey;
    let nextErrors = previousErrors;

    if (clientKey === '') {
        const existingError = previousErrors?.rooms?.[updateRoomIndex]?.guests?.[updatedGuestIndex]?.[fieldKey];
        if (existingError !== undefined) {
            nextErrors = setIn(nextErrors ?? {}, `rooms.${updateRoomIndex}.guests.${updatedGuestIndex}.${fieldKey}`, undefined);
        }
    } else {
        rooms.forEach((currentRoom, roomIndex) => {
            const roomHasLinkedGuest = currentRoom.guests.some((roomGuest) => roomGuest.clientKey === clientKey);
            if (!roomHasLinkedGuest) return;

            currentRoom.guests.forEach((guest, guestIndex) => {
                if (guest.clientKey !== clientKey) return;

                const existingError = previousErrors?.rooms?.[roomIndex]?.guests?.[guestIndex]?.[fieldKey];
                if (existingError === undefined) return;

                nextErrors = setIn(nextErrors ?? {}, `rooms.${roomIndex}.guests.${guestIndex}.${fieldKey}`, undefined);
            });
        });
    }

    return nextErrors;
}

/**
 * @param {Record<string, unknown>} previousErrors
 * @param {number} roomIndex
 * @param {number} guestIndex
 * @returns {Record<string, unknown>}
 */
export function clearFormikErrorsForGuest(previousErrors, roomIndex, guestIndex) {
    const roomErrors = previousErrors?.rooms?.[roomIndex];
    const guestErrors = roomErrors?.guests?.[guestIndex];
    const guestHasFormikErrors = guestErrors !== undefined && Object.keys(guestErrors).length > 0;

    if (!guestHasFormikErrors) {
        return previousErrors;
    }

    return setIn(previousErrors, `rooms.${roomIndex}.guests.${guestIndex}`, undefined);
}

/**
 * @param {Record<string, unknown>} previousErrors
 * @param {number} roomIndex
 * @param {number} removedGuestIndex
 * @returns {Record<string, unknown>}
 */
export function removeFormikErrorsGuestEntry(previousErrors, roomIndex, removedGuestIndex) {
    const guestsErrors = previousErrors?.rooms?.[roomIndex]?.guests;
    if (guestsErrors === undefined || guestsErrors.length === 0) {
        return previousErrors;
    }

    const nextGuestsErrors = guestsErrors.filter((_, guestIndex) => guestIndex !== removedGuestIndex);

    return setIn(previousErrors, `rooms.${roomIndex}.guests`, nextGuestsErrors);
}
